export const Gap: "-" = "-";

export type Pagination = (typeof Gap | number)[];

export const paginate = (
  page: number,
  perPage: number,
  resultsCount: number,
  windowSize: number = 4
): Pagination => {
  const windowBoundaries = { left: page + 2 - windowSize, right: page + 1 };
  const lastPage = Math.ceil(resultsCount / perPage);

  // If there's only one (or zero) pages to show, return a single page.
  if (lastPage <= 1) {
    return [1];
  }

  // We are on one of the first pages. We should list a couple of pages to the right.
  // Let's move the window so that it begins on page 2.
  if (windowBoundaries.left < 2) {
    windowBoundaries.left = 2;
    windowBoundaries.right = windowSize + 1;
  }

  // If the window ends on or after the last page we should move it to the left
  // and make it end just before the last page.
  if (windowBoundaries.right >= lastPage) {
    windowBoundaries.left = lastPage - windowSize;
    windowBoundaries.right = lastPage - 1;
  }

  // There's always a first page, let's add it.
  const pages: Pagination = [1];

  // If there's a gap between the window and first page, let's add a break between them.
  if (windowBoundaries.left > 2) {
    pages.push(Gap);
  }

  // This loop adds each page from the window to the shown pagination, in order.
  for (let i = windowBoundaries.left; i <= windowBoundaries.right; i++) {
    // If this page is outside the range of pages we have at our disposal skip it.
    if (i <= 1 || i >= lastPage) {
      continue;
    }
    pages.push(i);
  }

  // If there's a gap between end of window and the last page let's add a break.
  if (windowBoundaries.right < lastPage - 1) {
    pages.push(Gap);
  }

  // Finally, we always add the last page to the pagination.
  pages.push(lastPage);

  // Returning the sorted array of pages to show.
  return pages;
};
